import { Component, inject, signal, computed } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { 
  PerivisReportOptions, 
  PerivisReportPresets,
  ChartScope,
  SafetyFactorParameter,
  ConnectionSFParameter,
  BurstParameter,
  CollapseParameter,
  AxialForceParameter,
  ForceDisplacementParameter,
  validateReportOptions
} from '../../models/perivis-report-options.model';

interface PresetOption {
  label: string;
  value: string;
  options: PerivisReportOptions;
}

interface ChartConfigState {
  scope: ChartScope;
  parameter?: string;
  parameters?: string[];
  loadCaseNames?: string[];
}

@Component({
  selector: 'app-perivis-report-options-dialog',
  template: `
  <div class="flex flex-col">
    <p-scrollPanel [style]="{ width: '100%', height: '100%' }">
      <div class="flex flex-col space-y-2 max-h-[70vh]">
        <div class="mb-2">
          <label class="block">Quick Presets</label>
          <p-select
            class="w-full"
            [options]="presets"
            [(ngModel)]="selectedPreset"
            optionLabel="label"
            placeholder="Choose a preset"
            (onChange)="applyPreset($event.value)"
          ></p-select>
        </div>

        <p-divider></p-divider>

        <div>
          <h3 class="underline pb-2">Summary</h3>
          <div class="grid grid-cols-2 gap-2">
            <div class="flex items-center">
              <p-checkbox [(ngModel)]="options().includeMinSafetyFactorGrid" [binary]="true" inputId="minSfGrid" />
              <label for="minSfGrid" class="ml-2">Min Pipe Safety Factor Grid</label>
            </div>
            <div class="flex items-center">
              <p-checkbox [(ngModel)]="options().includeMinConnectionSFGrid" [binary]="true" inputId="minConnSfGrid" />
              <label for="minConnSfGrid" class="ml-2">Min Connection SF Grid</label>
            </div>
          </div>
        </div>

        <p-divider></p-divider>

        <div class="space-y-2">
          <div>
            <h3 class="underline pb-2">Plots</h3>
            <div class="grid grid-cols-2 gap-2">
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeDesignLimitsPlot" [binary]="true" inputId="designLimits" />
                <label for="designLimits" class="ml-2">Design Limits Plot</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includePackerEnvelopePlot" [binary]="true" inputId="packer" />
                <label for="packer" class="ml-2">Packer Operating Envelope</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeSafetyFactorPlot" [binary]="true" inputId="sfPlot" />
                <label for="sfPlot" class="ml-2">Pipe Safety Factors</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeConnectionSafetyFactorPlot" [binary]="true" inputId="connSF" />
                <label for="connSF" class="ml-2">Connection Safety Factors</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeBarlowBurstPlot" [binary]="true" inputId="burst" />
                <label for="burst" class="ml-2">Barlow Burst Pressure</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeApiCollapsePlot" [binary]="true" inputId="collapse" />
                <label for="collapse" class="ml-2">API Collapse Pressure</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeAxialForcePlot" [binary]="true" inputId="axial" />
                <label for="axial" class="ml-2">Axial Force with Bending</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeForceDisplacementPlot" [binary]="true" inputId="forceDisp" />
                <label for="forceDisp" class="ml-2">Force/Displacement Details</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includePressureTempPlot" [binary]="true" inputId="pressTemp" />
                <label for="pressTemp" class="ml-2">Pressure & Temperature Profiles</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeMaxAllowableWearPlot" [binary]="true" inputId="wear" />
                <label for="wear" class="ml-2">Max Allowable Wear</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeBucklingSummaryPlot" [binary]="true" inputId="buckling" />
                <label for="buckling" class="ml-2">Buckling Summary</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeLiftOffPlot" [binary]="true" inputId="liftOff" />
                <label for="liftOff" class="ml-2">Wellhead Hanger Lift-Off</label>
              </div>

            </div>

            <!-- Connection Safety Factor Chart Config -->
            @if (options().includeConnectionSafetyFactorPlot) {
              <app-chart-config
                  title="Connection Safety Factor Chart"
                  [config]="connectionSFConfig()"
                  [parameterOptions]="connectionSFParameters"
                  [loadCaseOptions]="loadCaseOptions()"
                  inputIdPrefix="csf"
                  (scopeChange)="onConnectionSafetyFactorScopeChange()"
                  (parametersChange)="onConnectionSFParametersChange($event)"
                  (loadCaseNamesChange)="onConnectionSFLoadCaseNamesChange($event)"
                />
            }

            <!-- Burst Chart Config -->
            @if (options().includeBarlowBurstPlot) {
              <app-chart-config
                  title="Burst Chart"
                  [config]="burstConfig()"
                  [parameterOptions]="burstParametersFiltered()"
                  [loadCaseOptions]="loadCaseOptions()"
                  inputIdPrefix="burst"
                  (scopeChange)="onBurstScopeChange()"
                  (parametersChange)="onBurstParametersChange($event)"
                  (loadCaseNamesChange)="onBurstLoadCaseNamesChange($event)"
                />
            }

            <!-- Collapse Chart Config -->
            @if (options().includeApiCollapsePlot) {
              <app-chart-config
                  title="Collapse Chart"
                  [config]="collapseConfig()"
                  [parameterOptions]="collapseParametersFiltered()"
                  [loadCaseOptions]="loadCaseOptions()"
                  inputIdPrefix="collapse"
                  (scopeChange)="onCollapseScopeChange()"
                  (parametersChange)="onCollapseParametersChange($event)"
                  (loadCaseNamesChange)="onCollapseLoadCaseNamesChange($event)"
                />
            }

            <!-- Axial Force Chart Config -->
            @if (options().includeAxialForcePlot) {
              <app-chart-config
                  title="Axial Force Chart"
                  [config]="axialForceConfig()"
                  [parameterOptions]="axialForceParametersFiltered()"
                  [loadCaseOptions]="loadCaseOptions()"
                  inputIdPrefix="axial"
                  (scopeChange)="onAxialForceScopeChange()"
                  (parametersChange)="onAxialForceParametersChange($event)"
                  (loadCaseNamesChange)="onAxialForceLoadCaseNamesChange($event)"
                />
            }

            <!-- Force Displacement Chart Config -->
            @if (options().includeForceDisplacementPlot) {
              <app-chart-config
                  title="Force Displacement Chart"
                  [config]="forceDisplacementConfig()"
                  [parameterOptions]="forceDisplacementParameters"
                  [loadCaseOptions]="loadCaseOptions()"
                  inputIdPrefix="fd"
                  (scopeChange)="onForceDisplacementScopeChange()"
                  (parametersChange)="onForceDisplacementParametersChange($event)"
                  (loadCaseNamesChange)="onForceDisplacementLoadCaseNamesChange($event)"
                />
            }
          </div>

          <p-divider></p-divider>

          <div>
            <h3 class="underline pb-2">Tables</h3>
            <div class="grid grid-cols-2 gap-2">
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeSafetyFactorTable" [binary]="true" inputId="sfTable" />
                <label for="sfTable" class="ml-2">Pipe Safety Factors</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeConnectionSafetyFactorTable" [binary]="true" inputId="connSFTable" />
                <label for="connSFTable" class="ml-2">Connection Safety Factors</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeBarlowBurstTable" [binary]="true" inputId="burstTable" />
                <label for="burstTable" class="ml-2">Barlow Burst Pressure</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeApiCollapseTable" [binary]="true" inputId="collapseTable" />
                <label for="collapseTable" class="ml-2">API Collapse Pressure</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeAxialForceTable" [binary]="true" inputId="axialTable" />
                <label for="axialTable" class="ml-2">Axial Force with Bending</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeForceDisplacementTable" [binary]="true" inputId="forceDispTable" />
                <label for="forceDispTable" class="ml-2">Force/Displacement Details</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeMaxAllowableWearTable" [binary]="true" inputId="wearTable" />
                <label for="wearTable" class="ml-2">Max Allowable Wear</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [(ngModel)]="options().includeExpansionJointTable" [binary]="true" inputId="ejTable" />
                <label for="ejTable" class="ml-2">Expansion Joint Results</label>
              </div>
            </div>

            <!-- Force Displacement Table Column Configuration -->
            @if (options().includeForceDisplacementTable) {
              <div class="mt-4 p-3 border-2 border-primary rounded">
                <div class="flex items-center gap-2 mb-2">
                  <i class="pi pi-table text-primary"></i>
                  <label class="block text-base underline">Force Displacement Table Columns</label>
                </div>
                <p-multiSelect
                  appendTo="body"
                  class="w-full"
                  [(ngModel)]="forceDisplacementTableColumns"
                  [options]="forceDisplacementParameters"
                  placeholder="Select columns to include in table"
                  display="chip"
                  [maxSelectedLabels]="4"
                  selectedItemsLabel="{0} items selected"
                  (onChange)="onForceDisplacementTableColumnsChange($event)"
                />
                <p class="text-xs mt-1"><i class="pi pi-info-circle mr-1"></i>Choose which data columns to include in the Force Displacement table output</p>
              </div>
            }

            <!-- Table Load Case Filter -->
            @if (options().includeConnectionSafetyFactorTable || options().includeBarlowBurstTable || options().includeApiCollapseTable || options().includeAxialForceTable || options().includeForceDisplacementTable || options().includeExpansionJointTable) {
              <div class="mt-4 p-3 border-2 border-primary rounded">
                <div class="flex items-center gap-2 mb-2">
                  <i class="pi pi-filter text-primary"></i>
                  <label class="block text-base underline">Table Load Case Filter</label>
                </div>
                <p-multiSelect
                  appendTo="body"
                  class="w-full"
                  [(ngModel)]="tableLoadCaseNames"
                  [options]="loadCaseOptions()"
                  placeholder="All load cases (leave empty to include all)"
                  display="chip"
                  [maxSelectedLabels]="4"
                  selectedItemsLabel="{0} items selected"
                  (onChange)="onTableLoadCaseNamesChange($event)"
                />
                <p class="text-xs mt-1"><i class="pi pi-info-circle mr-1"></i>Optionally restrict tables to specific load cases. Leave empty to output all load cases.</p>
              </div>
            }
          </div>

          <div>
            <!-- Pipe Safety Factor Chart Config -->
            @if (options().includeSafetyFactorPlot) {
              <app-chart-config
                  title="Pipe Safety Factor Chart"
                  [config]="safetyFactorConfig()"
                  [parameterOptions]="safetyFactorParameters"
                  [loadCaseOptions]="loadCaseOptions()"
                  inputIdPrefix="sf"
                  (scopeChange)="onSafetyFactorScopeChange()"
                  (parametersChange)="onSafetyFactorParametersChange($event)"
                  (loadCaseNamesChange)="onSafetyFactorLoadCaseNamesChange($event)"
                />
            }
          </div>
        </div>

        @if (validationErrors().length > 0) {
          <div class="p-3 bg-red-50 border border-red-300 rounded">
            <h4 class="font-semibold text-red-800 mb-2">Validation Errors:</h4>
            <ul class="list-disc list-inside text-sm text-red-700">
              @for (error of validationErrors(); track error) {
                <li>{{ error }}</li>
              }
            </ul>
          </div>
        }

      </div>
    </p-scrollPanel>
    
    <div class="flex justify-end gap-2 pt-4">
      <p-button label="Cancel" outlined="true" class="outlined-button" size="small" severity="danger" (onClick)="close()"></p-button>
      <p-button
        outlined="true"
        label="Save Setup"
        class="outlined-button"
        size="small"
        severity="success"
        icon="pi pi-save" 
        (onClick)="generate()"
        [disabled]="validationErrors().length > 0"
      ></p-button>
    </div>
  </div>
  `,
  standalone: false
})
export class PerivisReportOptionsDialogComponent {
  private readonly _dialogRef = inject(DynamicDialogRef);
  private readonly _dialogConfig = inject(DynamicDialogConfig);

  readonly options = signal<PerivisReportOptions>(PerivisReportPresets.None);
  selectedPreset: PresetOption | null = null;

  readonly presets: PresetOption[] = [
    { label: 'None (No Options Selected)', value: 'none', options: PerivisReportPresets.None },
    { label: 'Minimal', value: 'minimal', options: PerivisReportPresets.Minimal },
    { label: 'All Options', value: 'default', options: PerivisReportPresets.Default },
    { label: 'Plots Only', value: 'chartsOnly', options: PerivisReportPresets.ChartsOnly },
    { label: 'Safety Focused', value: 'safetyFocused', options: PerivisReportPresets.SafetyFocused }
  ];

  // Chart configuration signals
  readonly safetyFactorConfig = signal<ChartConfigState>({ scope: ChartScope.MultipleLoadCases, parameters: [SafetyFactorParameter.VonMisesEquivalent] });
  readonly connectionSFConfig = signal<ChartConfigState>({ scope: ChartScope.MultipleLoadCases, parameters: [ConnectionSFParameter.Burst] });
  readonly burstConfig = signal<ChartConfigState>({ 
    scope: ChartScope.MultipleLoadCases,
    parameters: [BurstParameter.BurstLoad]
  });
  readonly collapseConfig = signal<ChartConfigState>({ 
    scope: ChartScope.MultipleLoadCases,
    parameters: [CollapseParameter.CollapseLoad]
  });
  readonly axialForceConfig = signal<ChartConfigState>({ 
    scope: ChartScope.MultipleLoadCases,
    parameters: [AxialForceParameter.AxialLoad]
  });
  readonly forceDisplacementConfig = signal<ChartConfigState>({ scope: ChartScope.MultipleLoadCases, parameters: [ForceDisplacementParameter.InternalPressure] });

  // Table configuration
  forceDisplacementTableColumns: string[] = [];
  tableLoadCaseNames: string[] = [];

  // Parameter dropdown options
  readonly safetyFactorParameters = Object.values(SafetyFactorParameter);
  readonly connectionSFParameters = Object.values(ConnectionSFParameter);
  readonly burstParametersAll = Object.values(BurstParameter);
  readonly collapseParametersAll = Object.values(CollapseParameter);
  readonly axialForceParametersAll = Object.values(AxialForceParameter);
  readonly forceDisplacementParameters = Object.values(ForceDisplacementParameter);

  // Load case options from headersData
  public loadCaseOptions = computed(() => {
    const names: string[] = this._dialogConfig.data?.headersData?.loadCaseNames ?? [];
    return names.map(name => ({ label: name, value: name }));
  });

  // Filtered parameters (exclude "Both" for MultipleLoadCases)
  readonly burstParametersFiltered = computed(() => {
    return this.burstConfig().scope === ChartScope.SingleLoadCase
      ? this.burstParametersAll
      : this.burstParametersAll.filter(p => p !== BurstParameter.Both);
  });

  readonly collapseParametersFiltered = computed(() => {
    return this.collapseConfig().scope === ChartScope.SingleLoadCase
      ? this.collapseParametersAll
      : this.collapseParametersAll.filter(p => p !== CollapseParameter.Both);
  });

  readonly axialForceParametersFiltered = computed(() => {
    return this.axialForceConfig().scope === ChartScope.SingleLoadCase
      ? this.axialForceParametersAll
      : this.axialForceParametersAll.filter(p => p !== AxialForceParameter.Both);
  });

  // Validation errors
  readonly validationErrors = computed(() => {
    const opts = this.buildOptionsFromState();
    return validateReportOptions(opts);
  });

  constructor() {
    const initialOptions = this._dialogConfig.data?.initialOptions as PerivisReportOptions | null | undefined;

    if (initialOptions) {
      const clonedOptions = this.cloneOptions(initialOptions);
      this.options.set(clonedOptions);
      this.selectedPreset = null;
      this.loadChartConfigsFromOptions(clonedOptions);
      this.forceDisplacementTableColumns = clonedOptions.forceDisplacementTableColumns
        ? clonedOptions.forceDisplacementTableColumns.map(p => p as string)
        : [];
      this.tableLoadCaseNames = clonedOptions.tableLoadCaseNames ?? [];
      return;
    }

    const initialPreset = this._dialogConfig.data?.preset || 'none';
    this.selectedPreset = this.presets.find(p => p.value === initialPreset) || this.presets[0];
    const presetOptions = this.cloneOptions(this.selectedPreset.options);
    this.options.set(presetOptions);
    this.loadChartConfigsFromOptions(presetOptions);
    this.forceDisplacementTableColumns = presetOptions.forceDisplacementTableColumns
      ? presetOptions.forceDisplacementTableColumns.map(p => p as string)
      : [];
    this.tableLoadCaseNames = presetOptions.tableLoadCaseNames ?? [];
  }

  private cloneOptions(options: PerivisReportOptions): PerivisReportOptions {
    return JSON.parse(JSON.stringify(options));
  }

  applyPreset(preset: PresetOption): void {
    if (preset) {
      const clonedOptions = this.cloneOptions(preset.options);
      this.options.set(clonedOptions);
      this.loadChartConfigsFromOptions(clonedOptions);
      
      // Load table columns if provided
      if (clonedOptions.forceDisplacementTableColumns) {
        this.forceDisplacementTableColumns = clonedOptions.forceDisplacementTableColumns.map(p => p as string);
      } else {
        this.forceDisplacementTableColumns = [];
      }
      this.tableLoadCaseNames = clonedOptions.tableLoadCaseNames ?? [];
    }
  }

  private loadChartConfigsFromOptions(opts: PerivisReportOptions): void {
    if (opts.safetyFactorChartConfig) {
      this.safetyFactorConfig.set({
        scope: opts.safetyFactorChartConfig.scope,
        parameter: opts.safetyFactorChartConfig.parameter,
        parameters: opts.safetyFactorChartConfig.parameters,
        loadCaseNames: opts.safetyFactorChartConfig.loadCaseNames
      });
    }

    if (opts.connectionSafetyFactorChartConfig) {
      this.connectionSFConfig.set({
        scope: opts.connectionSafetyFactorChartConfig.scope,
        parameter: opts.connectionSafetyFactorChartConfig.parameter,
        parameters: opts.connectionSafetyFactorChartConfig.parameters,
        loadCaseNames: opts.connectionSafetyFactorChartConfig.loadCaseNames
      });
    }

    if (opts.burstChartConfig) {
      this.burstConfig.set({
        scope: opts.burstChartConfig.scope,
        parameter: opts.burstChartConfig.parameter,
        parameters: opts.burstChartConfig.parameters,
        loadCaseNames: opts.burstChartConfig.loadCaseNames
      });
    }

    if (opts.collapseChartConfig) {
      this.collapseConfig.set({
        scope: opts.collapseChartConfig.scope,
        parameter: opts.collapseChartConfig.parameter,
        parameters: opts.collapseChartConfig.parameters,
        loadCaseNames: opts.collapseChartConfig.loadCaseNames
      });
    }

    if (opts.axialForceChartConfig) {
      this.axialForceConfig.set({
        scope: opts.axialForceChartConfig.scope,
        parameter: opts.axialForceChartConfig.parameter,
        parameters: opts.axialForceChartConfig.parameters,
        loadCaseNames: opts.axialForceChartConfig.loadCaseNames
      });
    }

    if (opts.forceDisplacementChartConfig) {
      this.forceDisplacementConfig.set({
        scope: opts.forceDisplacementChartConfig.scope,
        parameter: opts.forceDisplacementChartConfig.parameter,
        parameters: opts.forceDisplacementChartConfig.parameters,
        loadCaseNames: opts.forceDisplacementChartConfig.loadCaseNames
      });
    }
  }

  // Scope change handlers to filter out "Both" parameter for MultipleLoadCases
  onSafetyFactorScopeChange(): void {
    // Safety Factor doesn't have "Both" option, so no filtering needed
  }

  onSafetyFactorParametersChange(parameters: string[] | undefined): void {
    const config = this.safetyFactorConfig();
    this.safetyFactorConfig.set({ ...config, parameters });
  }

  onSafetyFactorLoadCaseNamesChange(loadCaseNames: string[] | undefined): void {
    this.safetyFactorConfig.update(c => ({ ...c, loadCaseNames }));
  }

  onConnectionSafetyFactorScopeChange(): void {
    // Connection Safety Factor doesn't have "Both" option, so no filtering needed
  }

  onConnectionSFParametersChange(parameters: string[] | undefined): void {
    const config = this.connectionSFConfig();
    this.connectionSFConfig.set({ ...config, parameters });
  }

  onConnectionSFLoadCaseNamesChange(loadCaseNames: string[] | undefined): void {
    this.connectionSFConfig.update(c => ({ ...c, loadCaseNames }));
  }

  onBurstScopeChange(): void {
    setTimeout(() => {
      const config = this.burstConfig();
      const parameters = (config.scope === ChartScope.MultipleLoadCases || config.scope === ChartScope.Both)
        && config.parameters?.includes(BurstParameter.Both)
          ? config.parameters.filter(p => p !== BurstParameter.Both)
          : config.parameters;
      this.burstConfig.set({ ...config, parameters });
    }, 0);
  }

  onBurstParametersChange(parameters: string[] | undefined): void {
    const config = this.burstConfig();
    this.burstConfig.set({ ...config, parameters });
  }

  onBurstLoadCaseNamesChange(loadCaseNames: string[] | undefined): void {
    this.burstConfig.update(c => ({ ...c, loadCaseNames }));
  }

  onCollapseScopeChange(): void {
    setTimeout(() => {
      const config = this.collapseConfig();
      const parameters = (config.scope === ChartScope.MultipleLoadCases || config.scope === ChartScope.Both)
        && config.parameters?.includes(CollapseParameter.Both)
          ? config.parameters.filter(p => p !== CollapseParameter.Both)
          : config.parameters;
      this.collapseConfig.set({ ...config, parameters });
    }, 0);
  }

  onCollapseParametersChange(parameters: string[] | undefined): void {
    const config = this.collapseConfig();
    this.collapseConfig.set({ ...config, parameters });
  }

  onCollapseLoadCaseNamesChange(loadCaseNames: string[] | undefined): void {
    this.collapseConfig.update(c => ({ ...c, loadCaseNames }));
  }

  onAxialForceScopeChange(): void {
    setTimeout(() => {
      const config = this.axialForceConfig();
      const parameters = (config.scope === ChartScope.MultipleLoadCases || config.scope === ChartScope.Both)
        && config.parameters?.includes(AxialForceParameter.Both)
          ? config.parameters.filter(p => p !== AxialForceParameter.Both)
          : config.parameters;
      this.axialForceConfig.set({ ...config, parameters });
    }, 0);
  }

  onAxialForceParametersChange(parameters: string[] | undefined): void {
    const config = this.axialForceConfig();
    this.axialForceConfig.set({ ...config, parameters });
  }

  onAxialForceLoadCaseNamesChange(loadCaseNames: string[] | undefined): void {
    this.axialForceConfig.update(c => ({ ...c, loadCaseNames }));
  }

  onForceDisplacementScopeChange(): void {
    const config = this.forceDisplacementConfig();
    this.forceDisplacementConfig.set({ ...config });
  }

  onForceDisplacementParametersChange(parameters: string[] | undefined): void {
    const config = this.forceDisplacementConfig();
    this.forceDisplacementConfig.set({ ...config, parameters });
  }

  onForceDisplacementLoadCaseNamesChange(loadCaseNames: string[] | undefined): void {
    this.forceDisplacementConfig.update(c => ({ ...c, loadCaseNames }));
  }

  onTableLoadCaseNamesChange(event: any): void {
    this.tableLoadCaseNames = [...(event.value ?? [])];
  }

  onForceDisplacementTableColumnsChange(event: any): void {
    // Trigger change detection for validation
    this.forceDisplacementTableColumns = [...event.value];
  }

  private buildOptionsFromState(): PerivisReportOptions {
    const opts = { ...this.options() };

    // Build chart configs from current state - only include if chart is enabled
    if (opts.includeSafetyFactorPlot) {
      const config = this.safetyFactorConfig();
      opts.safetyFactorChartConfig = {
        scope: config.scope,
        parameter: config.parameter as SafetyFactorParameter | undefined,
        parameters: config.parameters?.map(p => p as SafetyFactorParameter),
        loadCaseNames: config.loadCaseNames
      };
    } else {
      opts.safetyFactorChartConfig = undefined;
    }

    if (opts.includeConnectionSafetyFactorPlot) {
      const config = this.connectionSFConfig();
      opts.connectionSafetyFactorChartConfig = {
        scope: config.scope,
        parameter: config.parameter as ConnectionSFParameter | undefined,
        parameters: config.parameters?.map(p => p as ConnectionSFParameter),
        loadCaseNames: config.loadCaseNames
      };
    } else {
      opts.connectionSafetyFactorChartConfig = undefined;
    }

    if (opts.includeBarlowBurstPlot) {
      const config = this.burstConfig();
      opts.burstChartConfig = {
        scope: config.scope,
        parameter: config.parameter as BurstParameter | undefined,
        parameters: config.parameters?.map(p => p as BurstParameter),
        loadCaseNames: config.loadCaseNames
      };
    } else {
      opts.burstChartConfig = undefined;
    }

    if (opts.includeApiCollapsePlot) {
      const config = this.collapseConfig();
      opts.collapseChartConfig = {
        scope: config.scope,
        parameter: config.parameter as CollapseParameter | undefined,
        parameters: config.parameters?.map(p => p as CollapseParameter),
        loadCaseNames: config.loadCaseNames
      };
    } else {
      opts.collapseChartConfig = undefined;
    }

    if (opts.includeAxialForcePlot) {
      const config = this.axialForceConfig();
      opts.axialForceChartConfig = {
        scope: config.scope,
        parameter: config.parameter as AxialForceParameter | undefined,
        parameters: config.parameters?.map(p => p as AxialForceParameter),
        loadCaseNames: config.loadCaseNames
      };
    } else {
      opts.axialForceChartConfig = undefined;
    }

    if (opts.includeForceDisplacementPlot) {
      const config = this.forceDisplacementConfig();
      opts.forceDisplacementChartConfig = {
        scope: config.scope,
        parameter: config.parameter as ForceDisplacementParameter | undefined,
        parameters: config.parameters?.map(p => p as ForceDisplacementParameter),
        loadCaseNames: config.loadCaseNames
      };
    } else {
      opts.forceDisplacementChartConfig = undefined;
    }

    // Include table configurations
    if (opts.includeForceDisplacementTable && this.forceDisplacementTableColumns.length > 0) {
      opts.forceDisplacementTableColumns = this.forceDisplacementTableColumns.map(c => c as ForceDisplacementParameter);
    } else if (!opts.includeForceDisplacementTable) {
      opts.forceDisplacementTableColumns = undefined;
    }

    opts.tableLoadCaseNames = this.tableLoadCaseNames.length > 0 ? this.tableLoadCaseNames : undefined;

    return opts;
  }

  generate(): void {
    const finalOptions = this.buildOptionsFromState();
    this._dialogRef.close(finalOptions);
  }

  close(): void {
    this._dialogRef.close(null);
  }
}
